import { useState } from 'react'
import { ORG_PLANS, formatBytes, type OrgPlan } from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Brandmark } from '../components/Brandmark.js'
import { useLanguage, useRichText, useText } from '../i18n.js'

const CHOOSABLE: OrgPlan[] = ['klein', 'team', 'gross']

/**
 * Selbstanmeldung von der Landingpage aus.
 *
 * Hier wird kein Passwort vergeben: Die Anmeldung erzeugt eine Organisation und
 * ein Konto, aber erst der Bestätigungslink aus der Mail macht daraus eine
 * Mitgliedschaft. Das hält erfundene Adressen draußen, ohne dass jemand ein
 * Passwort in ein Formular tippt, das er später nie wieder braucht.
 */
export function SignupPage(): React.ReactElement {
  const t = useText()
  const rich = useRichText()
  const { language } = useLanguage()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [orgName, setOrgName] = useState('')
  const [plan, setPlan] = useState<OrgPlan>('team')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await api.signup({ email, displayName, orgName, plan })
      setSent(true)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('common.failed'))
    } finally {
      setBusy(false)
    }
  }

  if (sent) {
    return (
      <div className="auth-screen brand-surface">
        <div className="auth-card">
          <Brandmark />
          <p className="auth-lead">
            {rich('signup.sent', {
              email: <strong>{email}</strong>,
              org: orgName || t('signup.yourOrg'),
            })}
          </p>
          <p className="muted small">{t('signup.spamHint')}</p>
          <a className="button ghost" href="/de/">
            {t('signup.backToOverview')}
          </a>
        </div>
      </div>
    )
  }

  const spec = ORG_PLANS[plan]

  return (
    <div className="auth-screen brand-surface">
      <form className="auth-card" onSubmit={submit}>
        <Brandmark />
        <p className="auth-lead">{t('signup.lead')}</p>

        <label className="field">
          <span>{t('redeem.yourName')}</span>
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            autoComplete="name"
            required
            placeholder={t('login.namePlaceholder')}
          />
        </label>

        <label className="field">
          <span>{t('common.organisation')}</span>
          <input
            value={orgName}
            onChange={(event) => setOrgName(event.target.value)}
            autoComplete="organization"
            required
            placeholder={t('signup.orgPlaceholder')}
          />
        </label>

        <label className="field">
          <span>{t('common.email')}</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </label>

        <label className="field">
          <span>{t('signup.plan')}</span>
          <select value={plan} onChange={(event) => setPlan(event.target.value as OrgPlan)}>
            {CHOOSABLE.map((key) => (
              <option key={key} value={key}>
                {t(ORG_PLANS[key].label)} — {t('signup.perMonth', { price: ORG_PLANS[key].monthlyEur ?? 0 })}
              </option>
            ))}
          </select>
          <small>
            {t('signup.planSummary', {
              members: spec.members,
              storage: formatBytes(spec.storageBytes, language),
            })}{' '}
            {t(spec.hint)} {t('signup.switchAnytime')}
          </small>
        </label>

        {error && <p className="form-error">{error}</p>}

        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('common.moment') : t('signup.submit')}
        </button>

        <a className="button link" href="/anmelden">
          {t('signup.haveAccount')}
        </a>
      </form>
    </div>
  )
}
